import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * InfoCardShell — expandable card layout for guide surfaces (streaks
 * explainer, upload guidelines, data & privacy).
 *
 * Iter-51.14 simplification: dropped the built-in Read-to-me and
 * "English में पढ़ें" toggle buttons that used to sit on every card.
 * Language is now selected once at the top of each page via the
 * shared GuideLangToggle (see `useGuideLang`), and TTS on these
 * informational surfaces was removed to save ElevenLabs cost as the
 * user base grows.
 *
 * Props:
 *   icon               – left-of-title badge (ReactNode)
 *   title, subtitle    – plain strings, ALWAYS in the language the
 *                        parent already picked via GuideLangToggle.
 *   contentLang        – 'en' | 'hi' — used only to apply the `font-hi`
 *                        typography class to the body when Hindi is
 *                        showing.
 *   testId             – data-testid stem for card / expand button /
 *                        body.
 *   defaultOpen        – card open on first render? Rewards/streaks
 *                        page passes true; upload/privacy pass false.
 *   children           – the card body, already in the correct
 *                        language by the parent.
 */
export default function InfoCardShell({
  icon,
  title,
  subtitle,
  contentLang = 'en',
  testId,
  children,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isHi = contentLang === 'hi';
  return (
    <div data-testid={`${testId}-card`} className="bg-white rounded-3xl p-6">
      <button type="button" onClick={() => setOpen(v => !v)}
        data-testid={`${testId}-expand`}
        className="w-full flex items-center gap-3 text-left">
        <div className="flex-shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className={`text-lg font-extrabold whitespace-normal break-words ${isHi ? 'font-hi' : ''}`}>{title}</div>
          {subtitle && <div className={`text-xs text-slate-500 ${isHi ? 'font-hi' : ''}`}>{subtitle}</div>}
        </div>
        {open ? <ChevronUp size={20} className="text-slate-400 flex-shrink-0"/>
              : <ChevronDown size={20} className="text-slate-400 flex-shrink-0"/>}
      </button>

      {open && (
        <div data-testid={`${testId}-body`}
          className={`mt-4 text-sm text-[#2B2140] space-y-3 ${isHi ? 'font-hi text-[0.95rem]' : ''}`}>
          {children}
        </div>
      )}
    </div>
  );
}
